import { type ReactNode, useMemo } from 'react'

import { PrintHeader } from '@/components/print/print-header'
import { useMeasurementPreference } from '@/hooks/chefs/useMeasurementPreference'
import { formatQuantity } from '@/lib/chefs/format'
import { type RecipePackagingTable, recipePackagingTables } from '@/lib/chefs/print-packages'
import { mergeDayRecipes, recipeBlockId } from '@/lib/chefs/print-plan'
import { displayAmount, type ScaleContext } from '@/lib/chefs/recipe-quantities'
import type { Campus, Recipe } from '@/lib/chefs/types'

interface PrintRecipePackProps {
  iso: string
  campuses: Campus[]
  /** The day's campuses in the summary's stop order, for the Packaging tables. Defaults to `campuses`. */
  packagingCampuses?: Campus[]
  kitchenName: string
  dateLabel: string
}

interface RecipeBlock {
  key: string
  label: string
  content: ReactNode
}

/** An empty box to tick when the container is packed. */
function CheckBox() {
  return <span aria-hidden className="mt-0.5 block h-3.5 w-3.5 rounded-[3px] border border-dashed border-[#888]" />
}

/**
 * The Packaging section (Abner, 137:2055: "Add this packaging section"): one
 * table per campus and mealtime, columns Check | Count | Package type |
 * Content. A bulk food gets one row per container; individually packaged
 * food one row per kind of container, with ↳ lines for what goes in it.
 */
export function RecipePackagingSection({ tables }: { tables: ReadonlyArray<RecipePackagingTable> }) {
  return (
    <div className="flex flex-col gap-4">
      {tables.map((table) => (
        <div key={table.key} data-testid="recipe-packaging-table" className="break-inside-avoid">
          <p className="text-sm">
            For <span className="font-semibold">{table.campusName}</span>
            <span className="ml-2 text-xs text-[#444]">
              {table.mealtimeLabel} · {table.temperature}
            </span>
          </p>
          <table className="mt-1 w-full table-fixed border-collapse text-xs">
            <colgroup>
              <col className="w-[48px]" />
              <col className="w-[48px]" />
              <col className="w-[34%]" />
              <col />
            </colgroup>
            <thead>
              <tr className="border-b border-black text-left">
                <th className="py-1 pr-2 font-semibold">Check</th>
                <th className="py-1 pr-2 font-semibold">Count</th>
                <th className="py-1 pr-2 font-semibold">Package type</th>
                <th className="py-1 font-semibold">Content</th>
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row) => (
                <tr key={row.key} data-testid="recipe-packaging-row" className="border-b border-black/15 align-top">
                  <td className="py-1.5 pr-2">
                    <CheckBox />
                  </td>
                  <td className="py-1.5 pr-2 font-semibold tabular-nums">{row.count}</td>
                  <td className="py-1.5 pr-2">{row.packageType}</td>
                  <td className="py-1.5">
                    {row.servings ? <span>{row.servings}</span> : null}
                    {row.each.map((item, i) => (
                      <span key={`${item.name}-${i}`} data-testid="recipe-packaging-each" className="block">
                        <span aria-hidden className="mr-1 text-[#666]">
                          ↳
                        </span>
                        {item.amount ? <span className="mr-1 font-semibold">{item.amount}</span> : null}
                        {item.name}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}

function buildBlocks(
  recipe: Recipe,
  scale: ScaleContext | null,
  packaging: ReadonlyArray<RecipePackagingTable> = [],
): RecipeBlock[] {
  const blocks: RecipeBlock[] = []

  // Scaled + converted amount for one authored quantity, or null → the
  // ingredient name renders alone (never a fabricated "0 unit"). Falls back
  // to the authored amount when the batch size is unknown.
  const amountFor = (amount: { quantity: number | null; unit: string | null; quantityBasis: string | null }) => {
    if (scale) {
      return displayAmount(amount, scale)
    }
    return amount.quantity !== null && amount.unit !== null ? `${amount.quantity} ${amount.unit}` : null
  }

  if (recipe.ingredients.length > 0) {
    blocks.push({
      key: 'ingredients',
      label: 'Ingredients',
      content: (
        <div className="flex flex-wrap gap-1.5">
          {recipe.ingredients.map((ing) => {
            // SCALED amounts on the chips — a scaled recipe is unusable in a
            // kitchen without the scaled weights (Yanping Ren, Ops).
            const label = amountFor(ing)
            return (
              <span
                key={ing.id}
                className="inline-flex items-center rounded-full border border-black/15 px-2 py-0.5 text-xs">
                {label ? <span className="mr-1 font-medium">{label}</span> : null}
                {ing.name}
              </span>
            )
          })}
        </div>
      ),
    })
  }

  blocks.push({
    key: 'recipe',
    label: 'Recipe',
    content: (
      <ol className="space-y-3">
        {recipe.steps.map((step, idx) => (
          <li key={step.id} className="-indent-4 pl-4">
            <span className="font-medium">{idx + 1}. </span>
            {step.text}
            {step.ingredientRefs.length > 0 ? (
              <span className="ml-4 mt-1 flex flex-wrap gap-1 indent-0">
                {step.ingredientRefs.map((ref, refIdx) => (
                  <span
                    key={refIdx}
                    className="inline-flex items-center rounded-full border border-black/15 px-1.5 py-px text-[10px] leading-tight">
                    {amountFor(ref) ? `${amountFor(ref)} ` : ''}
                    {ref.name}
                  </span>
                ))}
              </span>
            ) : null}
          </li>
        ))}
      </ol>
    ),
  })

  // Between Recipe and HACCP Points, as in the target print. The cookbook
  // print (no orders) has no tables, so no section.
  if (packaging.length > 0) {
    blocks.push({
      key: 'packaging',
      label: 'Packaging',
      content: <RecipePackagingSection tables={packaging} />,
    })
  }

  if (recipe.haccp.length > 0) {
    blocks.push({
      key: 'haccp',
      label: 'HACCP Points',
      content: (
        <ol className="space-y-2">
          {recipe.haccp.map((p, idx) => (
            <li key={p.id} className="-indent-4 pl-4">
              <span className="font-medium">{idx + 1}. </span>
              {p.text}
            </li>
          ))}
        </ol>
      ),
    })
  }

  if (recipe.tips && recipe.tips.length > 0) {
    blocks.push({
      key: 'tips',
      label: 'Tips',
      content: (
        <ul className="space-y-2">
          {recipe.tips.map((t) => (
            <li key={t.id} className="-indent-3 pl-3">
              · {t.text}
            </li>
          ))}
        </ul>
      ),
    })
  }

  return blocks
}

export interface PrintRecipeArticleProps {
  name: string
  /** Muted suffix after the title, e.g. "— makes 250 servings". */
  subtitle: string | null
  recipe: Recipe
  /** Batch scale for the quantities; null renders the authored amounts. */
  scale: ScaleContext | null
  kitchenName: string
  dateLabel: string
  /** `data-print-block` id for the measure host (packet prints only). */
  printBlockId?: string
  pageBreakAfter: boolean
  /** The Packaging section's tables (packet prints only); absent or empty prints no section. */
  packaging?: ReadonlyArray<RecipePackagingTable>
}

/**
 * One printed recipe page — header, title, and the Ingredients / Recipe /
 * HACCP / Tips sections. Shared by the kitchen-orders Scaled Recipes
 * packet (scaled to the day's orders) and the /recipes Print Cookbook
 * (authored amounts, `scale: null`).
 */
export function PrintRecipeArticle({
  name,
  subtitle,
  recipe,
  scale,
  kitchenName,
  dateLabel,
  printBlockId,
  pageBreakAfter,
  packaging,
}: PrintRecipeArticleProps) {
  const blocks = buildBlocks(recipe, scale, packaging)
  return (
    <article
      data-print-block={printBlockId}
      className={`font-sans text-black ${pageBreakAfter ? 'print-page-break' : ''}`}>
      <PrintHeader kitchenName={kitchenName} dateLabel={dateLabel} />

      <header className="mb-4 flex items-baseline justify-between gap-3 pb-1">
        <h1 className="text-2xl font-semibold">
          {name}
          {subtitle ? <span className="text-sm font-normal"> {subtitle}</span> : null}
        </h1>
      </header>

      {blocks.map((block, idx) => {
        const isLast = idx === blocks.length - 1
        return (
          <section
            key={block.key}
            className={isLast ? 'flex w-full' : 'mb-4 flex w-full border-b border-black/15 pb-4'}>
            <h2 className="w-[30%] shrink-0 pr-4 text-base font-medium">{block.label}</h2>
            <div className="w-[70%] flex-1 text-xs leading-relaxed">{block.content}</div>
          </section>
        )
      })}
    </article>
  )
}

export function PrintRecipePack({ iso, campuses, packagingCampuses, kitchenName, dateLabel }: PrintRecipePackProps) {
  // The printed packet follows the kitchen-wide Standard | Technical setting.
  const { mode } = useMeasurementPreference()
  const recipes = useMemo(() => mergeDayRecipes(campuses), [campuses])

  if (recipes.length === 0) {
    return null
  }

  return (
    <>
      {recipes.map((r, i) => {
        // Batch scale: the day's total servings over the recipe's base yield
        // (same math as the on-screen detail panel's RecipeScale). Only a
        // servings-unit total is a batch size; anything else renders the
        // authored amounts unscaled rather than guessing.
        const scale: ScaleContext | null =
          r.unit === 'x' && r.totalQty > 0 && r.component.recipe && r.component.recipe.baseYield > 0
            ? {
                servings: r.totalQty,
                baseYield: r.component.recipe.baseYield,
                servingMultiplier: r.component.servingMultiplier,
                system: 'imperial',
                mode,
              }
            : null
        return (
          <PrintRecipeArticle
            key={r.key}
            printBlockId={recipeBlockId(iso, r.key)}
            name={r.component.name}
            subtitle={
              r.unit === 'x'
                ? `— makes ${r.totalQty.toLocaleString()} servings`
                : `(${formatQuantity(r.totalQty, r.unit)} needed)`
            }
            recipe={r.component.recipe!}
            scale={scale}
            kitchenName={kitchenName}
            dateLabel={dateLabel}
            pageBreakAfter={i < recipes.length - 1}
            packaging={recipePackagingTables(packagingCampuses ?? campuses, r.key, iso)}
          />
        )
      })}
    </>
  )
}
